import React from 'react';
import _ from 'lodash';
import {
  DeleteOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import { imgUrl } from '../../utils/url';
import { message } from 'antd';

// Helper function to extract file name from a URL or File object
const extractFileName = (file) => {
  if (file.name) {
    return file.name; // For File objects or if name is already present
  }
  if (file.logo) {
    try {
      const url = file.logo; // This is assumed to be just the filename part or a full URL
      const segments = url.split('/');
      let fileName = segments[segments.length - 1];
      // Remove query parameters if any
      const queryIndex = fileName.indexOf('?');
      if (queryIndex > -1) {
        fileName = fileName.substring(0, queryIndex);
      }
      // Remove the uuid in the front of the logo name
      return fileName.replace(/^[a-f0-9-]+-/, "");
    } catch (e) {
      return 'Unknown File';
    }
  }
  return 'Unknown File';
};


const UploadedFilesPreview = ({ files, handleRemoveFile }) => {
  if (!files || files.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500 rounded-lg border border-dashed border-gray-300">
        No files uploaded yet.
      </div>
    );
  }

  // New handler for copying URL to clipboard
  const handleCopyUrlToClipboard = (url, fileName) => {
    if (!url) {
      console.warn(`Attempted to copy empty URL for ${fileName}`);
      return;
    }

    const textArea = document.createElement('textarea');
    textArea.value = url;
    textArea.style.position = 'fixed'; // Avoid scrolling to bottom
    textArea.style.opacity = '0'; // Hide it
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        message.success('URL copied to clipboard!')

      } else {
        console.error('Failed to copy URL using execCommand');
        message.error('Failed to copy URL')

      }
    } catch (err) {
      message.error('Failed to copy URL')
      console.error('Error in copying text:', err);

    } finally {
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="upload-chunk-wrapper p-4 space-y-4">
      {_.chunk(files, 3).map((chunk, chunkIndex) => (
        <div
          className="upload-chunk flex flex-wrap gap-4 justify-start"
          key={chunkIndex}
        >
          {chunk.map((file, fileIndexInChunk) => {
            // Calculate global index for handleRemoveFile
            const globalFileIndex = chunkIndex * 3 + fileIndexInChunk;

            // Determine file URL: prioritize remote URL, then client-side preview, then createObjectURL
            const fileUrl = file.logo
              ? `${imgUrl}/information/${file.logo}` // For files already on server (from backend)
              : file.preview // For client-side File objects with a pre-generated preview URL
              ? file.preview
              : file instanceof File // Fallback for newly selected raw File objects
              ? URL.createObjectURL(file)
              : ''; // Default empty if no identifiable source

            const fileType = (file.type || file.fileType || '').toLowerCase();
            const displayName = extractFileName(file);

            return (
              <div
                className="upload-file-wrapper relative w-full sm:w-1/3 md:w-1/4 lg:w-1/5 xl:w-1/6 flex-grow rounded-lg overflow-hidden shadow-md border border-gray-200 bg-white"
                key={globalFileIndex} // Using global index as key for stability
                style={{ minWidth: '150px', maxWidth: '200px' }} // Added inline style for better responsive control
              >
                {/* Remove button */}
                <button
                  type="button"
                  className="upload-remove-button absolute top-1 right-1 p-1 bg-red-100 rounded-full hover:bg-red-200 transition-colors z-10"
                  onClick={() => handleRemoveFile(globalFileIndex)}
                  aria-label={`Remove ${displayName}`}
                >
                  {/* Using Ant Design DeleteOutlined */}
                  <DeleteOutlined
                    style={{ color: '#CB3A31', fontSize: '18px' }}
                  />
                </button>

                {/* File Previews: Image and Video remain as original logic */}
                {fileType.startsWith('image/') && (
                  <img
                    alt={displayName}
                    src={fileUrl}
                    className="preview-image w-full h-32 object-cover"
                  />
                )}

                {fileType.startsWith('video/') && (
                  <video
                    controls
                    className="preview-video w-full h-32 object-cover bg-black"
                  >
                    <source src={fileUrl} type={fileType} />
                    Your browser does not support the video tag.
                  </video>
                )}

                {/* PDF File Preview (using Ant Design FilePdfOutlined) */}
                {fileType === 'application/pdf' && (
                  <div
                    className="pdf-preview flex flex-col items-center justify-center h-32 text-center p-2 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() =>
                      handleCopyUrlToClipboard(fileUrl, displayName)
                    }
                    title={`Click to copy URL: ${fileUrl}`}
                  >
                    {/* Using Ant Design FilePdfOutlined */}
                    <FilePdfOutlined
                      style={{
                        fontSize: '48px',
                        color: '#EF4444',
                        marginBottom: '4px',
                      }}
                    />
                    <p className="file-name-text text-sm font-medium text-gray-700 truncate w-full px-1">
                      {displayName}
                    </p>
                  </div>
                )}

                {/* Excel File Preview (using Ant Design FileExcelOutlined) */}
                {(fileType === 'application/vnd.ms-excel' ||
                  fileType ===
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') && (
                  <div
                    className="excel-preview flex flex-col items-center justify-center h-32 text-center p-2 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() =>
                      handleCopyUrlToClipboard(fileUrl, displayName)
                    }
                    title={`Click to copy URL: ${fileUrl}`}
                  >
                    {/* Using Ant Design FileExcelOutlined */}
                    <FileExcelOutlined
                      style={{
                        fontSize: '48px',
                        color: '#10B981',
                        marginBottom: '4px',
                      }}
                    />
                    <p className="file-name-text text-sm font-medium text-gray-700 truncate w-full px-1">
                      {displayName}
                    </p>
                  </div>
                )}

                {/* Generic File Preview for other types or unknown type with name */}
                {!(
                  fileType.startsWith('image/') ||
                  fileType.startsWith('video/') ||
                  fileType === 'application/pdf' ||
                  fileType.startsWith('application/vnd.ms-excel') ||
                  fileType.startsWith(
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                  )
                ) &&
                  (fileType !== '' || displayName !== 'Unknown File') && (
                    <div
                      className="excel-preview flex flex-col items-center justify-center h-32 text-center p-2 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() =>
                        handleCopyUrlToClipboard(fileUrl, displayName)
                      }
                      title={`Click to copy URL: ${fileUrl}`}
                    >

                      <p className="file-name-text text-sm font-medium text-gray-700 truncate w-full px-1">
                        {displayName}
                      </p>
                    </div>
                  )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default UploadedFilesPreview;
